/**
 * COMPANY DETAILS EDITOR
 * 
 * Modal component for editing company details.
 * Only editable fields are exposed; legal details are read-only.
 */

import React, { useState } from 'react';
import { getSessionHeaders } from '../../lib/serverSession'
import type { CompanyAccount, Address } from '../../types/company';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface CompanyDetailsEditorProps {
  company: CompanyAccount;
  onSave: () => Promise<void>;
  onClose: () => void;
}

export const CompanyDetailsEditor: React.FC<CompanyDetailsEditorProps> = ({
  company,
  onSave,
  onClose
}) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Editable fields
  const [tradingName, setTradingName] = useState(company.tradingName || '');
  const [businessDescription, setBusinessDescription] = useState(company.businessDescription || '');
  const [primaryEmail, setPrimaryEmail] = useState(company.primaryEmail || '');
  const [primaryPhone, setPrimaryPhone] = useState(company.primaryPhone || '');
  const [websiteUrl, setWebsiteUrl] = useState(company.websiteUrl || '');
  
  // Business address
  const [businessAddress, setBusinessAddress] = useState<Address>({
    street: company.businessAddress?.street || '',
    city: company.businessAddress?.city || '',
    province: company.businessAddress?.province || '',
    postalCode: company.businessAddress?.postalCode || '',
    countryCode: company.businessAddress?.countryCode || company.registeredAddress?.countryCode || ''
  });

  const handleSave = async () => {
    setError(null);
    setSaving(true);

    try {
      // Validate email
      if (primaryEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(primaryEmail)) {
        throw new Error('Please enter a valid email address');
      }

      // Prepare update data
      const updates: Record<string, any> = {
        trading_name: tradingName || null,
        business_description: businessDescription || null,
        primary_email: primaryEmail,
        primary_phone: primaryPhone || null,
        website_url: websiteUrl || null
      };

      // Add business address if provided
      if (businessAddress.street) {
        updates.business_address_street = businessAddress.street;
        updates.business_address_city = businessAddress.city;
        updates.business_address_province = businessAddress.province || null;
        updates.business_address_postal_code = businessAddress.postalCode;
        updates.business_address_country_code = businessAddress.countryCode;
      }

      const response = await fetch(`${API_BASE_URL}/api/user/company`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          userId: company.primaryDirectorUserId,
          operation: 'update_company_details',
          data: { companyId: company.id, updates }
        })
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error);

      await onSave();
      onClose();
    } catch (err: any) {
      console.error('Error saving company details:', err);
      setError(err.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-700">
          <h2 className="text-xl font-bold text-white">✏️ Edit Company Details</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl">×</button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {error && (
            <div className="bg-red-900/30 border border-red-500 rounded-lg p-4 text-red-400">
              {error}
            </div>
          )}

          {/* Read-only Legal Details Notice */}
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <p className="text-sm text-white flex items-center gap-2">
              <span>🔒</span>
              Legal details (Registration Number, Legal Name, Date of Incorporation) cannot be changed.
              Contact admin if corrections are needed.
            </p>
            <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
              <div><span className="text-gray-500">Legal Name:</span> <span className="text-white">{company.legalName}</span></div>
              <div><span className="text-gray-500">Reg. Number:</span> <span className="text-white">{company.registrationNumber}</span></div>
            </div>
          </div>

          {/* Trading Name */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">Trading Name (Optional)</label>
            <input
              type="text"
              value={tradingName}
              onChange={(e) => setTradingName(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-yellow-400 focus:outline-none"
              placeholder="Trading name / DBA"
            />
          </div>

          {/* Contact Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-white mb-2">Primary Email *</label>
              <input
                type="email"
                value={primaryEmail}
                onChange={(e) => setPrimaryEmail(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-yellow-400 focus:outline-none"
                placeholder="company@example.com"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-2">Primary Phone</label>
              <input
                type="tel"
                value={primaryPhone}
                onChange={(e) => setPrimaryPhone(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-yellow-400 focus:outline-none"
                placeholder="+27 11 123 4567"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-2">Website URL</label>
            <input
              type="url"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-yellow-400 focus:outline-none"
              placeholder="https://www.example.com"
            />
          </div>

          {/* Business Address */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">Business Address</label>
            <div className="space-y-3">
              <input
                type="text"
                value={businessAddress.street}
                onChange={(e) => setBusinessAddress({ ...businessAddress, street: e.target.value })}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-yellow-400 focus:outline-none"
                placeholder="Street address"
              />
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  value={businessAddress.city}
                  onChange={(e) => setBusinessAddress({ ...businessAddress, city: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-yellow-400 focus:outline-none"
                  placeholder="City"
                />
                <input
                  type="text"
                  value={businessAddress.province || ''}
                  onChange={(e) => setBusinessAddress({ ...businessAddress, province: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-yellow-400 focus:outline-none"
                  placeholder="Province/State"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  value={businessAddress.postalCode}
                  onChange={(e) => setBusinessAddress({ ...businessAddress, postalCode: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-yellow-400 focus:outline-none"
                  placeholder="Postal Code"
                />
                <input
                  type="text"
                  value={businessAddress.countryCode}
                  onChange={(e) => setBusinessAddress({ ...businessAddress, countryCode: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-yellow-400 focus:outline-none"
                  placeholder="Country Code (e.g., ZA)"
                />
              </div>
            </div>
          </div>

          {/* Business Description */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">Business Description</label>
            <textarea
              value={businessDescription}
              onChange={(e) => setBusinessDescription(e.target.value)}
              rows={4}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-yellow-400 focus:outline-none resize-none"
              placeholder="Describe your business activities..."
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-4 p-6 border-t border-gray-700">
          <button
            onClick={onClose}
            className="px-6 py-2 text-gray-400 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !primaryEmail}
            className="px-6 py-2 bg-yellow-500 hover:bg-yellow-400 text-black font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            {saving ? '⏳ Saving...' : '💾 Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompanyDetailsEditor;

